// The client's connection to a probe room, on any backend: the room URL from a MatchAssignment, the game's rpc
// client and msgpack codec, and the room's own close code when it ends.

import { RpcClient } from '@esengine/rpc'
import {
  createOnlineCodec,
  probeProtocol,
  type ProbeCommand,
  type ProbeOutcome,
  type ProbePong,
  type ProbeProtocol,
  type ProbeView,
  type ProbeWelcome,
} from '@taxwar/online'
import { BrowserSocket, SocketErrorScope, type SocketClose } from './browser-socket'

export interface ProbeLinkListener {
  welcome(welcome: ProbeWelcome): void
  view(view: ProbeView): void
  pong(pong: ProbePong): void
  ended(outcome: ProbeOutcome): void
  /** The connection is gone, with the room's (or the browser's) close code. Nothing follows. */
  closed(close: SocketClose): void
}

/** The room connection never opened (the socket failed before its handshake completed). */
export class RoomConnectError extends Error {
  override readonly name = 'RoomConnectError'
}

/** Browsers report a connection that died without a close frame as 1006. */
const ABNORMAL_CLOSE: SocketClose = { code: 1006, reason: 'connection dropped' }
/** The room ignores seq <= the last one it saw, starting from 0; the welcome moves this on for a reconnect. */
const FIRST_SEQ = 1

export class ProbeLink {
  private nextSeq = FIRST_SEQ
  private nextPing = 1
  private lastClose: SocketClose | null = null

  private constructor(private readonly client: RpcClient<ProbeProtocol>) {}

  static async connect(roomUrl: string, listener: ProbeLinkListener): Promise<ProbeLink> {
    let link: ProbeLink | null = null
    const errors = new SocketErrorScope()
    const client = new RpcClient(probeProtocol, roomUrl, {
      codec: createOnlineCodec(),
      webSocketFactory: (url) => new BrowserSocket(url, { closed: (close) => link?.recordClose(close), errors }),
      // A room is one match; a new connection needs a fresh claim anyway.
      autoReconnect: false,
      onDisconnect: () => listener.closed(link?.lastClose ?? ABNORMAL_CLOSE),
      // The socket's own error events end in a close (listener.closed); every other error is a bug.
      onError: (error) => {
        if (!errors.active) reportError(error)
      },
    })
    link = new ProbeLink(client)
    client.on('welcome', (welcome) => {
      link?.resumeAfter(welcome.lastSeq)
      listener.welcome(welcome)
    })
    client.on('view', (view) => listener.view(view))
    client.on('pong', (pong) => listener.pong(pong))
    client.on('ended', (outcome) => listener.ended(outcome))
    try {
      await client.connect()
    } catch {
      // RpcClient rejects a failed open with a plain Error("WebSocket error"): the try holds only the open, so
      // whatever it throws means the socket never opened.
      throw new RoomConnectError('the room connection could not be opened')
    }
    return link
  }

  /** Sends a command and returns its seq (views echo the last seq the room has seen). */
  sendCommand(command: ProbeCommand): number {
    const seq = this.nextSeq++
    this.client.send('command', { seq, command })
    return seq
  }

  /** Sends a latency probe and returns its id (the pong echoes it). */
  ping(): number {
    const id = this.nextPing++
    this.client.send('ping', { id })
    return id
  }

  dispose(): void {
    this.client.disconnect()
  }

  /** Commands continue after the last seq the room has seen from this seat (a reconnect). */
  private resumeAfter(lastSeq: number): void {
    this.nextSeq = Math.max(this.nextSeq, lastSeq + 1)
  }

  private recordClose(close: SocketClose): void {
    this.lastClose = close
  }
}
